<template>
  <section
    class="team-chat-panel"
    :aria-label="participant ? `Messages with ${participantName}` : 'Team messages'"
  >
    <template v-if="participant">
    <header class="chat-header">
      <div class="selected-avatar" aria-hidden="true">
        {{ initialsFor(participant) }}
      </div>
      <div>
        <h3>{{ participant?.name || participant?.email || "Team member" }}</h3>
        <p>
          <span class="inline-status" :class="{ online: isParticipantOnline }"></span>
          {{ isParticipantOnline ? "Online" : "Offline" }}
          <span v-if="participant?.email"> - {{ participant.email }}</span>
        </p>
      </div>
      <button
        type="button"
        class="icon-command"
        title="Close messages"
        aria-label="Close messages"
        @click="$emit('close')"
      >
        <span class="material-symbols-outlined" aria-hidden="true">close</span>
      </button>
    </header>

    <div ref="threadScroller" class="chat-thread" aria-live="polite">
      <div v-if="loading" class="chat-state">Loading messages.</div>
      <div v-else-if="error" class="chat-state error">{{ error }}</div>
      <div v-else-if="messages.length === 0" class="chat-state">
        No messages yet.
      </div>
      <template v-else>
        <article
          v-for="message in messages"
          :key="message.id"
          class="message-bubble"
          :class="{
            own: isOwnMessage(message),
            'has-reactions': hasReactions(message),
            'sticker-only': isStickerOnlyMessage(message.body),
          }"
          role="button"
          tabindex="0"
          :aria-label="`React to message from ${isOwnMessage(message) ? 'you' : participantName}`"
          @click="toggleReactionPicker(message)"
          @keydown.enter.prevent="toggleReactionPicker(message)"
          @keydown.space.prevent="toggleReactionPicker(message)"
        >
          <div
            v-if="openReactionMessageId === message.id"
            class="reaction-picker"
            aria-label="React to message"
          >
            <button
              v-for="emoji in reactionOptions"
              :key="emoji"
              type="button"
              :class="{ active: message.reactions?.[emoji]?.includes(currentUserId) }"
              :aria-label="`React with ${emoji}`"
              @click.stop="toggleReaction(message, emoji)"
            >
              {{ emoji }}
            </button>
          </div>
          <div class="bubble-meta">
            <strong>{{ isOwnMessage(message) ? "You" : participantName }}</strong>
            <span>{{ formatTime(message.createdAt) }}</span>
          </div>
          <p class="message-content">
            <template
              v-for="(segment, index) in renderMessageBody(message.body)"
              :key="`${message.id}-${index}`"
            >
              <img
                v-if="segment.type === 'sticker'"
                class="team-sticker-inline"
                :src="segment.sticker.src"
                :alt="segment.sticker.label"
                draggable="false"
              />
              <span v-else>{{ segment.text }}</span>
            </template>
          </p>
          <div v-if="hasReactions(message)" class="reaction-row">
            <button
              v-for="reaction in reactionEntries(message)"
              :key="reaction.emoji"
              type="button"
              :class="{ active: reaction.userIds.includes(currentUserId) }"
              @click="toggleReaction(message, reaction.emoji)"
            >
              {{ reaction.emoji }} {{ reaction.userIds.length }}
            </button>
          </div>
          <div v-if="message.references?.length" class="message-references" aria-label="Inventory references">
            <button
              v-for="reference in message.references"
              :key="`${message.id}-${reference.type}-${reference.id}`"
              type="button"
              class="team-reference-chip"
              :class="[`state-${reference.state}`, { navigable: reference.canNavigate }]"
              :disabled="!reference.canNavigate || !reference.navigation"
              :title="referenceTitle(reference)"
              @click.stop="openReference(reference)"
            >
              <span class="reference-icon" :style="{ '--reference-color': reference.color || '#d9e1eb' }" aria-hidden="true">
                <span class="material-symbols-outlined">{{ iconForReference(reference.type) }}</span>
              </span>
              <span class="reference-copy">
                <strong>{{ reference.label }}</strong>
                <small>{{ reference.detail || reference.labName }}</small>
              </span>
              <span v-if="reference.state !== 'available'" class="reference-state">
                {{ reference.state === "blocked" ? "No access" : "Missing" }}
              </span>
            </button>
          </div>
        </article>
      </template>
    </div>

    <form class="chat-composer" @submit.prevent="sendMessage">
      <div v-if="selectedReferences.length" class="draft-references" aria-label="Selected inventory references">
        <button
          v-for="reference in selectedReferences"
          :key="`${reference.type}-${reference.id}`"
          type="button"
          class="draft-reference-chip"
          :style="{ '--reference-color': reference.color || '#d9e1eb' }"
          :aria-label="`Remove ${reference.label} reference`"
          @click="removeReference(reference)"
        >
          <span class="material-symbols-outlined" aria-hidden="true">{{ iconForReference(reference.type) }}</span>
          <span>{{ reference.label }}</span>
          <span class="material-symbols-outlined remove-icon" aria-hidden="true">close</span>
        </button>
      </div>

      <div v-if="mentionActive" class="mention-picker" aria-label="Inventory mention search">
        <div v-if="mentionLoading" class="mention-state">Searching inventory...</div>
        <div v-else-if="mentionError" class="mention-state">{{ mentionError }}</div>
        <div v-else-if="!mentionQuery.trim()" class="mention-state">Type to search pucks, boxes, or grids.</div>
        <template v-else>
          <button
            v-for="result in mentionResults"
            :key="`${result.type}-${result.id}`"
            type="button"
            class="mention-result"
            @mousedown.prevent="selectMention(result)"
          >
            <span class="mention-icon" :style="{ '--reference-color': result.color || '#d9e1eb' }" aria-hidden="true">
              <span class="material-symbols-outlined">{{ iconForReference(result.type) }}</span>
            </span>
            <span class="mention-copy">
              <strong>{{ result.label }}</strong>
              <small>{{ result.detail || result.meta }}</small>
            </span>
            <span class="mention-type">{{ result.type }}</span>
          </button>
        </template>
        <div v-if="!mentionLoading && !mentionError && mentionQuery.trim() && mentionResults.length === 0" class="mention-state">
          No matching inventory items.
        </div>
      </div>

      <div class="composer-shell">
        <textarea
          ref="messageComposer"
          v-model="draft"
          rows="2"
          maxlength="2000"
          :placeholder="`Message ${participant?.name || 'this user'}`"
          :disabled="sending"
          @input="updateMentionState"
          @keyup="updateMentionState"
          @click="updateMentionState"
          @keydown.esc="mentionActive = false"
          @keydown.meta.enter.prevent="sendMessage"
          @keydown.ctrl.enter.prevent="sendMessage"
        ></textarea>
        <div class="composer-actions">
          <button
            type="button"
            class="sticker-toggle"
            :class="{ active: showStickerPicker }"
            aria-label="Add sticker"
            :aria-expanded="showStickerPicker"
            aria-controls="web-team-sticker-picker"
            @click="toggleStickerPicker"
          >
            <span class="material-symbols-outlined" aria-hidden="true">add_reaction</span>
          </button>
          <button type="submit" class="send-message-button" :disabled="!canSend">
            <span class="material-symbols-outlined" aria-hidden="true">send</span>
            Send
          </button>
        </div>
      </div>
      <div
        v-if="showStickerPicker"
        id="web-team-sticker-picker"
        class="sticker-picker"
        aria-label="Team stickers"
      >
        <button
          v-for="sticker in teamStickers"
          :key="sticker.id"
          type="button"
          class="sticker-option"
          :aria-label="`Insert ${sticker.label} sticker`"
          @click="insertSticker(sticker)"
        >
          <img :src="sticker.src" :alt="sticker.label" draggable="false" />
        </button>
      </div>
    </form>
    </template>

    <div v-else class="message-empty">
      <span class="material-symbols-outlined" aria-hidden="true">forum</span>
      <h2>Messages stay with the teammate.</h2>
      <p>Select a team member to open the conversation.</p>
    </div>
  </section>
</template>

<script>
import socket from "../../../socket";
import {
  TEAM_STICKERS,
  isStickerOnlyTeamMessage,
  splitTeamMessageBody,
} from "../../../constants/teamStickers";

const REACTION_OPTIONS = ["👍", "🔥", "❤️", "😂", "✅", "👀"];

export default {
  name: "TeamChatPanel",
  props: {
    participant: {
      type: Object,
      default: null,
    },
  },
  emits: ["summaryUpdated", "close"],
  data() {
    return {
      messages: [],
      loading: false,
      sending: false,
      error: "",
      draft: "",
      reactionOptions: REACTION_OPTIONS,
      teamStickers: TEAM_STICKERS,
      showStickerPicker: false,
      mentionActive: false,
      mentionQuery: "",
      mentionStart: null,
      mentionResults: [],
      mentionLoading: false,
      mentionError: "",
      mentionRequestId: 0,
      selectedReferences: [],
      openReactionMessageId: null,
      socketHandlersBound: false,
    };
  },
  computed: {
    participantId() {
      return this.canonicalId(this.participant);
    },
    currentUserId() {
      return this.canonicalId(this.appStore.state.activeUser);
    },
    participantName() {
      return this.participant?.name || this.participant?.email || "Team member";
    },
    isParticipantOnline() {
      return this.participant && this.appStore.state.usersOnline.has(this.participantId);
    },
    canSend() {
      const body = this.draft.trim();
      return Boolean(this.participantId && body && body.length <= 2000 && !this.sending);
    },
  },
  watch: {
    participantId: {
      immediate: true,
      handler() {
        this.messages = [];
        this.draft = "";
        this.showStickerPicker = false;
        this.mentionActive = false;
        this.mentionQuery = "";
        this.mentionResults = [];
        this.mentionError = "";
        this.selectedReferences = [];
        this.openReactionMessageId = null;
        this.loadThread();
      },
    },
  },
  mounted() {
    this.bindSocket();
  },
  beforeUnmount() {
    this.unbindSocket();
  },
  methods: {
    canonicalId(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      return value.id || "";
    },
    initialsFor(user) {
      return String(user?.name || user?.email || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "?";
    },
    requestUrl(path) {
      return `${this.appStore.state.hostname}/api/v1/messages${path}`;
    },
    iconForReference(type) {
      if (type === "puck") return "album";
      if (type === "box") return "inventory_2";
      return "grid_view";
    },
    referenceTitle(reference) {
      if (reference.state === "missing") return `${reference.label} is no longer available.`;
      if (reference.state === "blocked") return `You do not have access to ${reference.label}.`;
      return `Open ${reference.label} in Manage Products.`;
    },
    openReference(reference) {
      if (!reference?.canNavigate || !reference.navigation) return;
      this.$router.push({
        path: "/manage-products",
        query: { navigation: JSON.stringify(reference.navigation) },
      });
    },
    async request(path, options = {}) {
      const response = await fetch(this.requestUrl(path), {
        credentials: "include",
        headers: { "content-type": "application/json" },
        ...options,
      });
      const data = await response.json().catch(() => ({}));
      if (data.status === "fail" || data.status === "error" || !response.ok) {
        throw new Error(data.message || "Team messaging request failed.");
      }
      return data.data?.data;
    },
    async loadThread() {
      if (!this.participantId) return;
      this.loading = true;
      this.error = "";
      try {
        const data = await this.request(`/thread/${encodeURIComponent(this.participantId)}`);
        this.messages = data?.messages || [];
        await this.markThreadRead();
        this.scrollToBottom();
      } catch (error) {
        this.error = error.message || "Unable to load this thread.";
      } finally {
        this.loading = false;
      }
    },
    async sendMessage() {
      if (!this.canSend) return;
      const body = this.draft.trim();
      const references = this.selectedReferences.map((reference) => ({
        type: reference.type,
        id: reference.id,
      }));
      this.sending = true;
      this.error = "";
      try {
        const message = await this.request("/", {
          method: "POST",
          body: JSON.stringify({
            recipientId: this.participantId,
            body,
            references,
          }),
        });
        this.upsertMessage(message);
        this.draft = "";
        this.selectedReferences = [];
        this.mentionActive = false;
        this.mentionQuery = "";
        this.mentionResults = [];
        this.showStickerPicker = false;
        this.scrollToBottom();
      } catch (error) {
        this.error = error.message || "Unable to send message.";
      } finally {
        this.sending = false;
      }
    },
    async markThreadRead() {
      if (!this.participantId || !this.messages.length) return;
      const lastMessage = this.messages[this.messages.length - 1];
      try {
        const summary = await this.request(
          `/thread/${encodeURIComponent(this.participantId)}/read`,
          {
            method: "PATCH",
            body: JSON.stringify({ upToMessageId: lastMessage.id }),
          }
        );
        this.$emit("summaryUpdated", summary);
      } catch {
        // A read marker failure should not hide an otherwise readable thread.
      }
    },
    async toggleReaction(message, emoji) {
      try {
        const updated = await this.request(`/${encodeURIComponent(message.id)}/reaction`, {
          method: "PATCH",
          body: JSON.stringify({ emoji }),
        });
        this.upsertMessage(updated);
        this.openReactionMessageId = null;
      } catch (error) {
        this.error = error.message || "Unable to update reaction.";
      }
    },
    bindSocket() {
      if (this.socketHandlersBound) return;
      this.socketHandlersBound = true;
      socket.on("team-message:new", this.handleIncomingMessage);
      socket.on("team-message:sent", this.handleSentMessage);
      socket.on("team-message:reaction", this.handleReactionMessage);
      socket.on("team-message:unread-summary", this.handleUnreadSummary);
    },
    unbindSocket() {
      socket.off("team-message:new", this.handleIncomingMessage);
      socket.off("team-message:sent", this.handleSentMessage);
      socket.off("team-message:reaction", this.handleReactionMessage);
      socket.off("team-message:unread-summary", this.handleUnreadSummary);
    },
    handleIncomingMessage(payload) {
      if (payload?.unreadSummary) {
        this.$emit("summaryUpdated", payload.unreadSummary);
      }
      if (!this.messageBelongsToThread(payload?.message)) return;
      this.upsertMessage(payload.message);
      this.markThreadRead();
      this.scrollToBottom();
    },
    handleSentMessage(payload) {
      if (!this.messageBelongsToThread(payload?.message)) return;
      this.upsertMessage(payload.message);
      this.scrollToBottom();
    },
    handleReactionMessage(payload) {
      if (!this.messageBelongsToThread(payload?.message)) return;
      this.upsertMessage(payload.message);
    },
    handleUnreadSummary(summary) {
      this.$emit("summaryUpdated", summary);
    },
    messageBelongsToThread(message) {
      if (!message || !this.participantId || !this.currentUserId) return false;
      return (
        (message.senderUserId === this.currentUserId &&
          message.recipientUserId === this.participantId) ||
        (message.senderUserId === this.participantId &&
          message.recipientUserId === this.currentUserId)
      );
    },
    upsertMessage(message) {
      if (!message?.id) return;
      const existingIndex = this.messages.findIndex((entry) => entry.id === message.id);
      if (existingIndex >= 0) {
        this.messages[existingIndex] = message;
      } else {
        this.messages.push(message);
        this.messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      }
    },
    isOwnMessage(message) {
      return message.senderUserId === this.currentUserId;
    },
    hasReactions(message) {
      return this.reactionEntries(message).length > 0;
    },
    reactionEntries(message) {
      return Object.entries(message.reactions || {})
        .filter(([, userIds]) => Array.isArray(userIds) && userIds.length)
        .map(([emoji, userIds]) => ({ emoji, userIds }));
    },
    toggleReactionPicker(message) {
      this.showStickerPicker = false;
      this.openReactionMessageId = this.openReactionMessageId === message.id ? null : message.id;
    },
    toggleStickerPicker() {
      this.openReactionMessageId = null;
      this.mentionActive = false;
      this.showStickerPicker = !this.showStickerPicker;
    },
    insertSticker(sticker) {
      const needsSpace = this.draft.length > 0 && !/\s$/.test(this.draft);
      this.draft = `${this.draft}${needsSpace ? " " : ""}${sticker.token} `;
      this.showStickerPicker = false;
      this.$nextTick(() => this.$refs.messageComposer?.focus());
    },
    currentMentionRange() {
      const textarea = this.$refs.messageComposer;
      const end = textarea?.selectionStart ?? this.draft.length;
      const beforeCursor = this.draft.slice(0, end);
      const match = beforeCursor.match(/(^|\s)@([^\s@]{0,48})$/);
      if (!match) return null;
      const query = match[2] || "";
      return {
        start: end - query.length - 1,
        end,
        query,
      };
    },
    updateMentionState() {
      const range = this.currentMentionRange();
      if (!range) {
        this.mentionActive = false;
        this.mentionQuery = "";
        this.mentionStart = null;
        return;
      }
      this.mentionActive = true;
      this.showStickerPicker = false;
      this.mentionQuery = range.query;
      this.mentionStart = range.start;
      if (range.query.trim()) {
        this.fetchMentionResults(range.query);
      } else {
        this.mentionResults = [];
        this.mentionError = "";
      }
    },
    async fetchMentionResults(query) {
      const trimmed = query.trim();
      if (!trimmed) return;
      const requestId = this.mentionRequestId + 1;
      this.mentionRequestId = requestId;
      this.mentionLoading = true;
      this.mentionError = "";
      try {
        const encoded = encodeURIComponent(trimmed);
        const results = await this.request(`/references/search?q=${encoded}&limit=8`, { method: "GET" });
        if (this.mentionRequestId === requestId) {
          this.mentionResults = Array.isArray(results) ? results : [];
        }
      } catch (error) {
        if (this.mentionRequestId === requestId) {
          this.mentionResults = [];
          this.mentionError = error.message || "Inventory search is unavailable.";
        }
      } finally {
        if (this.mentionRequestId === requestId) {
          this.mentionLoading = false;
        }
      }
    },
    selectMention(result) {
      const range = this.currentMentionRange();
      const start = range?.start ?? this.mentionStart ?? this.draft.length;
      const end = range?.end ?? this.draft.length;
      const token = `@${result.label} `;
      this.draft = `${this.draft.slice(0, start)}${token}${this.draft.slice(end)}`;
      if (!this.selectedReferences.some((reference) => reference.type === result.type && reference.id === result.id)) {
        this.selectedReferences.push(result);
      }
      this.mentionActive = false;
      this.mentionQuery = "";
      this.$nextTick(() => {
        const cursor = start + token.length;
        this.$refs.messageComposer?.focus();
        this.$refs.messageComposer?.setSelectionRange(cursor, cursor);
      });
    },
    removeReference(reference) {
      this.selectedReferences = this.selectedReferences.filter((candidate) => (
        candidate.type !== reference.type || candidate.id !== reference.id
      ));
    },
    renderMessageBody(body) {
      const segments = splitTeamMessageBody(body);
      return segments.length ? segments : [{ type: "text", text: "" }];
    },
    isStickerOnlyMessage(body) {
      return isStickerOnlyTeamMessage(body);
    },
    formatTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.threadScroller;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.team-chat-panel {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

.chat-header {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) 36px;
  gap: 0.85rem;
  align-items: center;
  min-height: 84px;
  border-bottom: 1px solid #dbe4ee;
  padding: 1rem 1.2rem;
  background: #fff;
}

.selected-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: #dbeafe;
  color: #335c8b;
  font-size: 0.9rem;
  font-weight: 900;
}

.chat-header h3 {
  margin: 0;
  overflow: hidden;
  color: #111827;
  font-size: 1.08rem;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-header p {
  margin: 0.2rem 0 0;
  overflow: hidden;
  color: #64748b;
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inline-status {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 0.28rem;
  border-radius: 999px;
  background: #94a3b8;
}

.inline-status.online {
  background: #097021;
}

.icon-command {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin: 0;
  border: 1px solid #cbd7e6;
  border-radius: 7px;
  background: #f8fbff;
  color: #334155;
  cursor: pointer;
}

.chat-thread {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.85rem;
  min-height: 0;
  overflow-y: auto;
  padding: 1.4rem clamp(1.25rem, 4vw, 3.5rem);
  background: #f8fbff;
}

.chat-state {
  display: grid;
  place-items: center;
  min-height: 190px;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 800;
}

.chat-state.error {
  color: #b42318;
}

.message-bubble {
  position: relative;
  align-self: flex-start;
  width: auto;
  max-width: min(66ch, 74%);
  border: 1px solid #d7e2ef;
  border-radius: 13px;
  background: #fff;
  padding: 0.72rem 0.86rem;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.05);
  cursor: pointer;
  outline: 0;
}

.message-bubble.has-reactions {
  margin-bottom: 1.35rem;
}

.message-bubble:hover,
.message-bubble:focus-visible {
  border-color: #b8cadf;
  box-shadow:
    0 1px 2px rgb(15 23 42 / 0.07),
    0 0 0 3px rgb(51 92 139 / 0.08);
}

.message-bubble.own {
  align-self: flex-end;
  border-color: #9bc6e3;
  background: #eaf6ff;
  color: #172033;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.08);
}

.bubble-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 800;
}

.bubble-meta strong {
  color: #0f3654;
}

.message-bubble p {
  margin: 6px 0 0;
  color: #172033;
  overflow-wrap: anywhere;
  font-size: 0.95rem;
  line-height: 1.38;
  white-space: pre-wrap;
}

.message-content span {
  white-space: pre-wrap;
}

.team-sticker-inline {
  width: 2.8rem;
  height: 2.8rem;
  margin: 0 0.08rem;
  border-radius: 7px;
  object-fit: cover;
  vertical-align: middle;
  box-shadow: 0 2px 8px rgb(15 23 42 / 0.18);
}

.message-bubble.sticker-only {
  padding: 0.5rem;
}

.message-bubble.sticker-only .message-content {
  text-align: center;
}

.message-bubble.sticker-only .team-sticker-inline {
  width: 6rem;
  height: 6rem;
  margin: 0;
  border-radius: 10px;
}

.reaction-row {
  position: absolute;
  bottom: -1rem;
  left: 0.86rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  max-width: calc(100% - 1.72rem);
  margin: 0;
  padding: 0;
}

.message-bubble.own .reaction-row {
  right: 0.86rem;
  left: auto;
  justify-content: flex-end;
}

.reaction-picker {
  display: inline-flex;
  position: absolute;
  top: calc(100% + 0.36rem);
  bottom: auto;
  left: 0;
  z-index: 25;
  gap: 0.2rem;
  padding: 0.28rem;
  border: 1px solid #d9e2ec;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 12px 26px rgb(15 23 42 / 0.18);
}

.message-bubble.own .reaction-picker {
  right: 0;
  left: auto;
}

.reaction-row button,
.reaction-picker button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: 0;
  height: 22px;
  margin: 0;
  border: 1px solid #dfe7ef;
  border-radius: 999px;
  background: #fff;
  color: #334155;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);
  cursor: pointer;
}

.reaction-row button {
  gap: 0.16rem;
  padding: 0 0.42rem;
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1;
}

.reaction-picker button {
  width: 30px;
  height: 30px;
  padding: 0;
  font-size: 1rem;
}

.reaction-row button.active,
.reaction-picker button.active,
.reaction-picker button:hover,
.reaction-picker button:focus-visible {
  border-color: #0b4f71;
  background: #eef6ff;
  color: #0b4f71;
}

.message-references {
  display: flex;
  flex-wrap: wrap;
  max-width: 100%;
  margin-top: 0.38rem;
}

.team-reference-chip {
  display: inline-grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.42rem;
  max-width: 100%;
  min-height: 40px;
  padding: 0.28rem 0.5rem 0.28rem 0.32rem;
  border: 1px solid #d6e0eb;
  border-radius: 999px;
  background: #fff;
  color: #172033;
  font: inherit;
  text-align: left;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);
}

.team-reference-chip.navigable {
  cursor: pointer;
}

.team-reference-chip:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.team-reference-chip.navigable:hover,
.team-reference-chip.navigable:focus-visible {
  border-color: #335c8b;
  outline: 0;
  box-shadow: 0 0 0 3px rgb(51 92 139 / 0.12);
}

.reference-icon,
.mention-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--reference-color) 28%, white);
  color: #335c8b;
}

.reference-icon .material-symbols-outlined,
.mention-icon .material-symbols-outlined {
  font-size: 16px;
}

.reference-copy,
.mention-copy {
  display: grid;
  min-width: 0;
}

.reference-copy strong,
.reference-copy small,
.mention-copy strong,
.mention-copy small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reference-copy strong,
.mention-copy strong {
  color: #172033;
  font-size: 0.8rem;
  font-weight: 900;
}

.reference-copy small,
.reference-state,
.mention-copy small {
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 750;
}

.reference-state,
.mention-type {
  border-radius: 999px;
  background: #eef2f7;
  padding: 0.12rem 0.38rem;
  color: #526176;
  font-size: 0.63rem;
  font-weight: 900;
  text-transform: uppercase;
}

.chat-composer {
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 0.55rem;
  border-top: 1px solid #dbe4ee;
  padding: 1rem 1.2rem 1.15rem;
  background: #fff;
}

.draft-references {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.draft-reference-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  max-width: 100%;
  min-height: 28px;
  padding: 0 0.42rem;
  border: 1px solid #d6e0eb;
  border-radius: 999px;
  background: #f8fbff;
  color: #172033;
  font: inherit;
  font-size: 0.76rem;
  font-weight: 850;
  cursor: pointer;
}

.draft-reference-chip .material-symbols-outlined {
  color: #335c8b;
  font-size: 15px;
}

.draft-reference-chip .remove-icon {
  color: #64748b;
  font-size: 14px;
}

.mention-picker {
  display: grid;
  position: absolute;
  right: 1.2rem;
  bottom: calc(100% + 0.5rem);
  left: 1.2rem;
  z-index: 45;
  gap: 0.25rem;
  max-height: min(360px, 46vh);
  overflow-y: auto;
  padding: 0.45rem;
  border: 1px solid #d5dfec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 36px rgb(15 23 42 / 0.18);
}

.mention-result {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
  min-height: 46px;
  padding: 0.35rem 0.45rem;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #172033;
  text-align: left;
  cursor: pointer;
}

.mention-result:hover,
.mention-result:focus-visible {
  background: #eef6ff;
  outline: 0;
}

.mention-state {
  padding: 0.65rem 0.7rem;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 800;
}

.message-empty {
  display: grid;
  flex: 1;
  place-items: center;
  align-content: center;
  gap: 0.4rem;
  min-height: 420px;
  padding: 2rem;
  color: #64748b;
  text-align: center;
}

.message-empty .material-symbols-outlined {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  border-radius: 999px;
  background: #eef6ff;
  color: #335c8b;
  font-size: 28px;
}

.message-empty h2 {
  margin: 0.45rem 0 0;
  color: #111827;
  font-size: 1rem;
  font-weight: 900;
}

.message-empty p {
  margin: 0;
  font-size: 0.86rem;
}

.composer-shell {
  display: flex;
  gap: 0.65rem;
  align-items: stretch;
}

.chat-composer textarea {
  flex: 1;
  min-width: 0;
  resize: vertical;
  min-height: 54px;
  border: 1px solid #cbd7e6;
  border-radius: 7px;
  padding: 0.8rem 0.85rem;
  background: #fff;
  color: #172033;
  font: inherit;
  font-size: 0.94rem;
  line-height: 1.35;
}

.chat-composer textarea:focus {
  border-color: #335c8b;
  outline: 0;
  box-shadow: 0 0 0 3px rgb(51 92 139 / 0.14);
}

.composer-actions {
  display: flex;
  gap: 0.45rem;
}

.sticker-toggle,
.send-message-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  margin: 0;
  border: 0;
  border-radius: 7px;
  font-weight: 900;
  cursor: pointer;
}

.sticker-toggle {
  min-width: 48px;
  border: 1px solid #cbd7e6;
  background: #f8fbff;
  color: #335c8b;
}

.sticker-toggle.active,
.sticker-toggle:hover,
.sticker-toggle:focus-visible {
  border-color: #335c8b;
  background: #eef6ff;
}

.send-message-button {
  gap: 6px;
  min-width: 86px;
  background: #335c8b;
  padding: 0 1.1rem;
  color: #fff;
}

.send-message-button:disabled {
  border-color: #cbd7e6;
  background: #94a3b8;
  cursor: not-allowed;
}

.sticker-picker {
  display: grid;
  position: absolute;
  right: 12px;
  bottom: calc(100% + 0.5rem);
  z-index: 40;
  grid-template-columns: repeat(5, 46px);
  gap: 0.38rem;
  width: max-content;
  max-width: calc(100% - 24px);
  max-height: min(320px, 45vh);
  overflow-y: auto;
  padding: 0.5rem;
  border: 1px solid #d9e2ec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 24px rgb(15 23 42 / 0.12);
}

.sticker-option {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  padding: 0;
  border: 1px solid #d9e2ec;
  border-radius: 7px;
  background: #fff;
  cursor: pointer;
}

.sticker-option:hover,
.sticker-option:focus-visible {
  border-color: #335c8b;
  box-shadow: 0 0 0 3px rgb(51 92 139 / 0.14);
}

.sticker-option img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
}

@media (max-width: 760px) {
  .message-bubble {
    max-width: min(34rem, 84%);
  }
}
</style>
